import { useEffect, useRef, useState } from "react";

import {
    WrapperAdvancedSearch,
    SectionHeading,
    TableCustom,
} from "../../components/common";
import { ALERT_TYPE, OPERATION_MODE, REQUEST_METHOD, ShowToastMessage } from '@laramie/ui';
import { ServerRequest } from "../../utils/apiweb";
import { APIS } from "../../config/apis";
import { useLista, useManagedContext, useNav, useFilterPersistence, useNavigateWithFilters } from "../../components/hooks";
import DataTaggerModalApi from "../../components/controls/DataTaggerModalApi";
import { MESSAGE_MODALS } from "../../context/custom/MessageModalContext";
import { PERMISSIONS } from "../../consts/permissions";
import { usePermissions } from "../../components/hooks/usePermissions";
import {TIPS} from "../../consts/tips";

const ProcedimientosView = () => {
    const navigate = useNav();
    const { showMessageModal, setIsLoading } = useManagedContext();

    const [mounted, setMounted] = useState(false);
    const [data, setData] = useState([]);
    const [tableVisible, setTableVisible] = useState(false);
    const [dataTaggerState, setDataTaggerState] = useState({});

    const searchRef = useRef();
    const tableRef = useRef();

    const SearchProcedimientosWithSavedFilters = () => {
        if (searchRef.current) {
            fetchData();
        }
    };

    const { getCurrentFilters, restoredPageIndex, restoredGlobalFilter } = useFilterPersistence(
        searchRef,
        tableRef,
        SearchProcedimientosWithSavedFilters
    );

    const navigateWithFilters = useNavigateWithFilters(getCurrentFilters);

    const { hasPermission } = usePermissions();
    const canEdit = hasPermission(PERMISSIONS.PROCEDIMIENTO_EDIT);

    const [, getRowLista] = useLista({
        listas: ["TipoTributo"],
        onLoaded: (listas, isSuccess, error) => {
            if (!isSuccess) {
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error);
            }
        },
        memo: {
            key: "ProcedimientoTipoTributo",
            timeout: 0,
        },
    });

    const getTipoTributo = (id) => getRowLista("TipoTributo", id) ?? {};

    const onNoSuccess = (response) => {
        response
            .json()
            .then((error) => {
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error.message);
                setIsLoading(false);
            })
            .catch((error) => {
                ShowToastMessage(
                    ALERT_TYPE.ALERT_ERROR,
                    "Error procesando respuesta: " + error
                );
                setIsLoading(false);
            });
    };

    const onError = (error) => {
        ShowToastMessage(
            ALERT_TYPE.ALERT_ERROR,
            "Error procesando solicitud: " + error.message
        );
        setIsLoading(false);
    };

    const filterData = (rawData) => {
        const { idTipoTributo } = searchRef.current.filters;

        let filtered = [...rawData];
        if (idTipoTributo !== 0) {
            filtered = filtered.filter((x) => x.idTipoTributo === idTipoTributo);
        }
        return filtered;
    };

    const fetchData = () => {
        setIsLoading(true);
        ServerRequest(
            REQUEST_METHOD.GET,
            null,
            true,
            APIS.URLS.PROCEDIMIENTO,
            null,
            null,
            (res) =>
                res.json().then((data) => {
                    const filtered = filterData(data);
                    setData(filtered);
                    setIsLoading(false);
                    setTableVisible(true);
                }),
            onNoSuccess,
            onError
        );
    };

    useEffect(() => {
        if (!mounted) {
            setMounted(true);
        } else {
            fetchData();
        }
    }, []);

    const onSearch = () => {
        fetchData();
    };

    const avmr = {
        onAdd: () => navigateWithFilters(`/procedimiento/${OPERATION_MODE.NEW}`),
        onView: ({ id }) => navigateWithFilters(`/procedimiento/${OPERATION_MODE.VIEW}/${id}`),
        onModify: ({ id }) => navigateWithFilters(`/procedimiento/${OPERATION_MODE.EDIT}/${id}`),
        onClone: ({ id }) => {
            const procedimiento = data.find((x) => x.id === id);
            showMessageModal({
                ...MESSAGE_MODALS.CLONAR,
                message: `¿Está seguro de clonar el procedimiento "${procedimiento.nombre}"?`,
                onConfirm: () => onCloneId(id),
            });
        },
        onRemove: ({ id }) => {
            const procedimiento = data.find((x) => x.id === id);
            showMessageModal({
                ...MESSAGE_MODALS.BORRAR,
                message: `¿Está seguro de borrar el procedimiento "${procedimiento.nombre}"?`,
                onConfirm: () => onConfirmRemoveId(id),
            });
        },
        onAdditionalInfo: ({ id }, disabled) => {
            setDataTaggerState({ id, disabled });
        },
    };

    const onCloneId = (id) => {
        setIsLoading(true);
        ServerRequest(
            REQUEST_METHOD.PUT,
            null,
            true,
            APIS.URLS.PROCEDIMIENTO,
            `/clone/${id}`,
            null,
            (res) => {
                ShowToastMessage(ALERT_TYPE.ALERT_SUCCESS, "Procedimiento clonado");
                onSearch();
            },
            onNoSuccess,
            onError
        );
    };

    const onConfirmRemoveId = (id) => {
        setIsLoading(true);
        ServerRequest(
            REQUEST_METHOD.DELETE,
            null,
            true,
            APIS.URLS.PROCEDIMIENTO,
            `/${id}`,
            null,
            (res) => {
                setData((prev) => prev.filter((x) => x.id !== id));
                setIsLoading(false);
                ShowToastMessage(ALERT_TYPE.ALERT_SUCCESS, "Procedimiento borrado");
            },
            onNoSuccess,
            onError
        );
    };

    const dataTaggerRegistro = data.find(x => x.id === dataTaggerState.id)?.nombre ?? ''

    return (
        <div>
            <SectionHeading
                titles={["Emisión", "Procedimientos de emisión"]}
                onAddClick={canEdit ? avmr.onAdd : null}
                tableVisible={tableVisible}
            />

            <section className="section-accordion">
                <WrapperAdvancedSearch
                    ref={searchRef}
                    filters={[
                        {
                            title: "Tipo de tributo",
                            field: "idTipoTributo",
                            type: "list",
                            lista: "TipoTributo",
                            size: 2,
                        },
                    ]}
                    onSearch={onSearch}
                />

                {tableVisible && (
                    <TableCustom
                        ref={tableRef}
                        restoredPageIndex={restoredPageIndex}
                        defaultGlobalFilter={restoredGlobalFilter}
                        className={"TableCustomBase m-top-10"}
                        data={data}
                        messageEmpty="No se encontraron resultados"
                        columns={[
                            {
                                Header: "Tipo de tributo",
                                accessor: "idTipoTributo",
                                Cell: ({ value }) => getTipoTributo(value).nombre ?? "",
                                width: "30%",
                            },
                            { Header: "Nombre", accessor: "nombre", width: "30%" },
                            { Header: "Descripción", accessor: "descripcion", width: "30%" },
                        ]}
                        avmr={avmr}
                        editCode={PERMISSIONS.PROCEDIMIENTO_EDIT}
                    />
                )}
            </section>

            {dataTaggerState.id && (
                <DataTaggerModalApi
                    title="Información adicional de procedimiento"
                    registro={dataTaggerRegistro}
                    tipName={TIPS.NAME.MODALS.INFORMACION_ADICIONAL_PROCEDIMIENTO_MODAL}
                    entidad="Procedimiento"
                    idEntidad={dataTaggerState.id}
                    disabled={dataTaggerState.disabled}
                    onConfirm={() => setDataTaggerState({})}
                    onDismiss={() => setDataTaggerState({})}
                />
            )}
        </div>
    );
};

export default ProcedimientosView;
